import {
  AgentNameAnswer,
  AgentNameProblem,
  type CreateProjectBody,
  CreateProjectBody as CreateProjectBodySchema,
  type CreateRepoBody,
  NeedsYouSummary,
  PlatformMinds,
  ProjectListItem,
  RepoView,
  RunnerListItemV1,
  RunnerSharedProfile,
} from "@minga/protocol"
import { queryOptions } from "@tanstack/react-query"
import { Option, Schema } from "effect"
import { runnersQuery } from "@/features/runners/runner-api"
import { ApiError, apiJson } from "./api"

/**
 * The integration-wave HTTP routes the web reads (spec §6.1): H1–H5, H8 and H10. Every response is decoded with the
 * shared schemas from packages/protocol (P10), the same ones the server encodes with.
 */

const enc = encodeURIComponent
const ws = (workspaceId: string) => `/api/workspaces/${enc(workspaceId)}`

export const ProjectList = Schema.Struct({ projects: Schema.Array(ProjectListItem) })
export const ProjectCreated = Schema.Struct({ project: ProjectListItem })
export const RepoList = Schema.Struct({ repos: Schema.Array(RepoView) })
export const RepoCreated = Schema.Struct({ repo: RepoView })

// ---- H1 / H2: projects ---------------------------------------------------------------------------------------------

export const projectsQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "projects"],
    queryFn: async () => (await apiJson(`${ws(workspaceId)}/projects`, ProjectList)).projects,
    staleTime: 30_000,
  })

/** H2. The body is encoded with the protocol schema (it trims the name and checks every field). */
export const createProject = async (workspaceId: string, body: CreateProjectBody) =>
  (
    await apiJson(`${ws(workspaceId)}/projects`, ProjectCreated, {
      method: "POST",
      body: Schema.encodeSync(CreateProjectBodySchema)(body),
    })
  ).project

// ---- decision 0166 §4: agent names unique per workspace ----------------------------------------------------------

/**
 * A free name for a new lead (`seed`: the sheet's command id, so it keeps one suggestion while open) and what is wrong
 * with `name`/`handle` (with `agent` and its own name and handle: its rename hint). Debounce typed values before.
 */
export const agentNameQuery = (
  workspaceId: string,
  q: { readonly seed?: string; readonly name?: string; readonly handle?: string; readonly agent?: string },
) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "agent-name", q],
    queryFn: () => {
      const params = new URLSearchParams()
      for (const [k, v] of Object.entries(q)) if (typeof v === "string") params.set(k, v)
      return apiJson(`${ws(workspaceId)}/agent-name?${params}`, AgentNameAnswer)
    },
    staleTime: 30_000,
    retry: false,
  })

// ---- decision 0129: project members ------------------------------------------------------------------------------

const Ok = Schema.Struct({ ok: Schema.Boolean })
const project = (workspaceId: string, projectId: string) => `${ws(workspaceId)}/projects/${enc(projectId)}`

/** Add a workspace member to the project, or change their role (project admins). */
export const setProjectMember = (
  workspaceId: string,
  projectId: string,
  userId: string,
  role: "admin" | "member",
) =>
  apiJson(`${project(workspaceId, projectId)}/members/${enc(userId)}`, Ok, {
    method: "PUT",
    body: { role },
  })

/** Take someone out of the project (project admins), or leave it (yourself). */
export const removeProjectMember = (workspaceId: string, projectId: string, userId: string) =>
  apiJson(`${project(workspaceId, projectId)}/members/${enc(userId)}`, Ok, { method: "DELETE" })

/** Join a workspace-visible project as a member. */
export const joinProject = (workspaceId: string, projectId: string) =>
  apiJson(`${project(workspaceId, projectId)}/join`, Ok, { method: "POST" })

/** Plain-language text for a failed H2. `outdated` = the picked runner must update first (B11). */
export const createProjectError = (
  error: Error,
): {
  readonly message: string
  readonly outdated: boolean
  readonly field: "slug" | "repo" | "mind" | "leadName" | null
  /** Decision 0166 §4 (`lead_name_taken`): a free name and handle to offer. */
  readonly suggestion?: { readonly name: string; readonly handle: string }
} => {
  if (!(error instanceof ApiError)) return { message: error.message, outdated: false, field: null }
  switch (error.body?.error) {
    case "lead_name_taken": {
      const problem = Option.getOrNull(
        Schema.decodeUnknownOption(AgentNameProblem)((error.body as { readonly problem?: unknown }).problem),
      )
      return problem
        ? { message: problem.message, outdated: false, field: "leadName", suggestion: problem.suggestion }
        : {
            message: "Another agent in this workspace has this name. Pick another.",
            outdated: false,
            field: "leadName",
          }
    }
    case "slug_taken":
      return {
        message: "A project with this short name already exists. Pick another name.",
        outdated: false,
        field: "slug",
      }
    case "unknown_repo":
      return {
        message: "That repo isn't registered in this workspace any more.",
        outdated: false,
        field: "repo",
      }
    case "unknown_runner":
      return { message: "That runner isn't paired with this workspace.", outdated: false, field: "mind" }
    case "runner_not_owned":
      return { message: "A lead can only think on your own runner.", outdated: false, field: "mind" }
    case "runner_outdated":
      return {
        message: "That runner runs an older Minga. Update it (Runners → Update), then try again.",
        outdated: true,
        field: "mind",
      }
    case "rejected":
      return {
        message: "The project couldn't be created as entered. Check the fields and try again.",
        outdated: false,
        field: null,
      }
    case "invalid_body":
      return {
        message: "Some fields are invalid. Check the name, handle and notes.",
        outdated: false,
        field: null,
      }
    default:
      return { message: error.message, outdated: false, field: null }
  }
}

// ---- H3 / H4: repos -----------------------------------------------------------------------------------------------

export const reposQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "repos"],
    queryFn: async () => (await apiJson(`${ws(workspaceId)}/repos`, RepoList)).repos,
    staleTime: 30_000,
  })

export const createRepo = async (workspaceId: string, body: CreateRepoBody) =>
  (await apiJson(`${ws(workspaceId)}/repos`, RepoCreated, { method: "POST", body })).repo

export const createRepoError = (error: Error): string =>
  error instanceof ApiError && error.body?.error === "invalid_remote"
    ? "That doesn't look like a git remote. Use a URL like git@github.com:org/repo.git or github.com/org/repo."
    : error.message

// ---- H5: platform minds --------------------------------------------------------------------------------------------

export const mindsQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "minds"],
    queryFn: () => apiJson(`${ws(workspaceId)}/minds`, PlatformMinds),
    staleTime: 5 * 60_000,
    retry: false,
  })

// ---- H8: runners (v1 fields) ----------------------------------------------------------------------------------------

const decodeRunner = Schema.decodeUnknownOption(RunnerListItemV1)
const decodeSharedProfile = Schema.decodeUnknownOption(RunnerSharedProfile)

/** H8 items, decoded one by one: a runner this page can't read is dropped on its own. */
export const decodeRunners = (json: unknown): ReadonlyArray<RunnerListItemV1> =>
  Array.isArray(json) ? json.flatMap((item) => Option.toArray(decodeRunner(item))) : []

/**
 * A runner row of `GET …/runners` (as `runnersQuery` holds it) as a v1 item. Fields a row may leave out (a runner first
 * seen on the live push, a redacted row, an older server) read as null / false; a row that still doesn't decode is dropped.
 */
export const toRunnerV1 = (row: object): ReadonlyArray<RunnerListItemV1> => {
  const r = row as Record<string, unknown>
  const seen = r.lastSeenAt
  return Option.toArray(
    decodeRunner({
      manifest: null,
      desiredConfig: null,
      pendingUpdate: null,
      updateStatus: null,
      latestVersion: null,
      updateAvailable: false,
      ...r,
      lastSeenAt:
        typeof seen === "number" ? seen : typeof seen === "string" ? Date.parse(seen) || null : null,
      // decision 0168: entry by entry, so one odd login never drops the runner
      ...(Array.isArray(r.sharedProfiles)
        ? { sharedProfiles: r.sharedProfiles.flatMap((p) => Option.toArray(decodeSharedProfile(p))) }
        : {}),
    }),
  )
}

/** The whole list as v1 items (stable, so a `select` with it is memoized per fetch). */
export const toRunnersV1 = (rows: ReadonlyArray<object>): ReadonlyArray<RunnerListItemV1> =>
  rows.flatMap(toRunnerV1)

/**
 * H8 as v1 items, derived from the one runner list (`runnersQuery`): same key, same fetch, no poll of its own. The live
 * push (`use-runners-live.ts`) keeps that list current.
 */
export const runnersV1Query = (workspaceId: string) =>
  queryOptions({ ...runnersQuery(workspaceId), select: toRunnersV1 })

// ---- H10: Needs-you summary ------------------------------------------------------------------------------------------

/** How fresh Home's copy of Needs-you must be to stand in for the header's own request (decision 0151). */
export const HOME_NEEDS_YOU_FRESH_MS = 10_000

export const needsYouSummaryQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "needs-you"],
    // Decision 0151: Home's answer carries the same Needs-you (one fan-out to every project). While Home is loading or
    // just loaded, the header's count takes it from there instead of asking every project again.
    queryFn: async ({ client }): Promise<NeedsYouSummary> => {
      // a Home mounted in the same commit starts its request in this task: let it, then look
      await new Promise((resolve) => setTimeout(resolve, 0))
      const homeKey = ["workspaces", workspaceId, "home"] as const
      const home = client.getQueryCache().find<{ readonly needsYou: NeedsYouSummary }>({
        queryKey: homeKey,
        exact: true,
      })
      if (home?.state.fetchStatus === "fetching" && home.promise) {
        const data = await home.promise.catch(() => null)
        if (data?.needsYou) return data.needsYou
      } else if (home?.state.data && Date.now() - home.state.dataUpdatedAt < HOME_NEEDS_YOU_FRESH_MS) {
        return home.state.data.needsYou
      }
      return apiJson(`${ws(workspaceId)}/needs-you`, NeedsYouSummary)
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  })
